import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { IErrorResponse } from '@/shared/types/interfaces';
import { toast } from 'react-toastify';

type RequestType = 'get' | 'post' | 'put' | 'delete' | 'patch';
type URLParams = { [key: string]: string | number };
type RequestConfig = AxiosRequestConfig & { urlParams?: URLParams; formUrlencoded?: boolean };
type ThunkRequest<TResponse, TPayload> = (
    payload?: TPayload | RequestConfig,
    config?: AxiosRequestConfig | RequestConfig
) => Promise<TResponse>;

function createInstance(): AxiosInstance {
    const axiosInstance = axios.create({ baseURL: import.meta.env.VITE_APP_SERVICE_URL });
    return axiosInstance;
}

export const http: AxiosInstance = createInstance();

const formatURLParams = (actionName: string, params: URLParams) =>
    Object.entries(params).reduce((acc, [key, value]) => acc.replace(`:${key}`, `${value}`), actionName);

const getContentType = (requestConfig: RequestConfig) => {
    if (requestConfig.formUrlencoded) return 'application/x-www-form-urlencoded';
    return undefined;
};

export const makeRequest = async <TResponse, TPayload = unknown>(
    type: RequestType,
    controllerName: string,
    actionName = '',
    payload: TPayload | RequestConfig = {},
    config: RequestConfig = {}
): Promise<TResponse> => {
    const requestConfig = (['get', 'delete'].includes(type) ? payload : config) as RequestConfig;
    const additionalHeaders = requestConfig.headers || {};
    requestConfig.headers = { 'Content-Type': getContentType(requestConfig), ...additionalHeaders };
    const formattedActionName = requestConfig?.urlParams ? formatURLParams(actionName, requestConfig.urlParams) : actionName;
    const path = `${controllerName}/${formattedActionName}`;
    const response = await http[type](path, payload, {
        ...requestConfig
    });

    return response.data;
};

export const thunkify = <TResponse = void, TArg = void, TErrorResponse = IErrorResponse, TPayload = unknown>(
    type: RequestType,
    controllerName: string,
    actionName: string = '',
    requestCb?: (thunkRequest: ThunkRequest<TResponse, TPayload>, arg: TArg) => Promise<TResponse>
): AsyncThunk<TResponse, TArg, { rejectValue: TErrorResponse }> => {
    return createAsyncThunk<TResponse, TArg, { rejectValue: TErrorResponse }>(
        `${type}#${controllerName}/${actionName || ''}`,
        async (arg, { rejectWithValue }) => {
            if (!navigator.onLine) {
                toast("Oops! You aren't connected to the Internet", {
                    type: 'error',
                    autoClose: false,
                    icon: undefined
                });
            }

            const thunkRequest = _.partial(makeRequest, type, controllerName, actionName) as ThunkRequest<TResponse, TArg | TPayload>;

            const request = requestCb ? requestCb(thunkRequest, arg) : thunkRequest(arg);
            return request.catch((err: AxiosError) => rejectWithValue(err.response?.data as TErrorResponse));
        }
    );
};

/*

    return request.catch((err: AxiosError) => {
                return rejectWithValue({
                    requestMetaData: {
                        baseUrl: err?.config?.baseURL || '',
                        method: err?.config?.method || '',
                        url: err?.config?.url || ''
                    },
                    code: err.response?.status,
                    message: err.message || '',
                    errorData: err.response?.data
                } as TErrorResponse);
    });

*/
